import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { EditorMode, ViewMode, UIPanel, ContextMenuState } from '../types';

interface UIStore {
  // State
  editorMode: EditorMode;
  viewMode: ViewMode;
  panels: UIPanel;
  contextMenu: ContextMenuState;
  sidebarCollapsed: boolean;
  propertiesPanelWidth: number;
  objectLibraryWidth: number;

  // UI operations
  setEditorMode: (mode: Partial<EditorMode>) => void;
  setViewMode: (mode: Partial<ViewMode>) => void;
  togglePanel: (panel: keyof UIPanel) => void;
  setPanelState: (panel: keyof UIPanel, state: boolean) => void;

  // Context menu
  showContextMenu: (x: number, y: number, type: ContextMenuState['type'], objectId?: string) => void;
  hideContextMenu: () => void;

  // Layout operations
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setPropertiesPanelWidth: (width: number) => void;
  setObjectLibraryWidth: (width: number) => void;

  // Utility operations
  resetUI: () => void;
  getActiveMode: () => string;
  isPanelOpen: (panel: keyof UIPanel) => boolean;
}

const DEFAULT_EDITOR_MODE: EditorMode = {
  mode: 'select',
  space: 'world',
  snap: false,
  snapValue: 1,
};

const DEFAULT_VIEW_MODE: ViewMode = {
  type: 'editor',
  showLabels: true,
  showWireframe: false,
  showBounds: false,
};

const DEFAULT_PANELS: UIPanel = {
  objectLibrary: true,
  hierarchy: true,
  properties: true,
  timeline: false,
  inspector: false,
};

const DEFAULT_CONTEXT_MENU: ContextMenuState = {
  visible: false,
  x: 0,
  y: 0,
  type: 'empty',
};

export const useUIStore = create<UIStore>()(
  immer((set, get) => ({
    // Initial state
    editorMode: DEFAULT_EDITOR_MODE,
    viewMode: DEFAULT_VIEW_MODE,
    panels: DEFAULT_PANELS,
    contextMenu: DEFAULT_CONTEXT_MENU,
    sidebarCollapsed: false,
    propertiesPanelWidth: 320,
    objectLibraryWidth: 280,

    // UI operations
    setEditorMode: (mode) => {
      set((state) => {
        Object.assign(state.editorMode, mode);
      });
    },

    setViewMode: (mode) => {
      set((state) => {
        Object.assign(state.viewMode, mode);
      });
    },

    togglePanel: (panel) => {
      set((state) => {
        state.panels[panel] = !state.panels[panel];
      });
    },

    setPanelState: (panel, panelState) => {
      set((state) => {
        state.panels[panel] = panelState;
      });
    },

    // Context menu
    showContextMenu: (x, y, type, objectId) => {
      set((state) => {
        state.contextMenu = {
          visible: true,
          x,
          y,
          type,
          objectId,
        };
      });
    },

    hideContextMenu: () => {
      set((state) => {
        state.contextMenu = DEFAULT_CONTEXT_MENU;
      });
    },

    // Layout operations
    toggleSidebar: () => {
      set((state) => {
        state.sidebarCollapsed = !state.sidebarCollapsed;
      });
    },

    setSidebarCollapsed: (collapsed) => {
      set((state) => {
        state.sidebarCollapsed = collapsed;
      });
    },

    setPropertiesPanelWidth: (width) => {
      set((state) => {
        state.propertiesPanelWidth = Math.max(200, Math.min(600, width));
      });
    },

    setObjectLibraryWidth: (width) => {
      set((state) => {
        state.objectLibraryWidth = Math.max(200, Math.min(400, width));
      });
    },

    // Utility operations
    resetUI: () => {
      set((state) => {
        state.editorMode = DEFAULT_EDITOR_MODE;
        state.viewMode = DEFAULT_VIEW_MODE;
        state.panels = DEFAULT_PANELS;
        state.contextMenu = DEFAULT_CONTEXT_MENU;
        state.sidebarCollapsed = false;
        state.propertiesPanelWidth = 320;
        state.objectLibraryWidth = 280;
      });
    },

    getActiveMode: () => {
      const { editorMode } = get();
      return editorMode.mode;
    },

    isPanelOpen: (panel) => {
      const { panels } = get();
      return panels[panel];
    },
  })),
);
